import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { api } from "@/logic/api";
import {
  ReleaseFlag,
  useReleaseManagement,
} from "@/utility/feature-management";
import { useSession } from "@/utility/useSession";
import { useApiWithReject } from "@/utility/utils";
import { createLiveUpdatesClient } from "./liveUpdatesClient";
import { LiveUpdatesClientContext } from "./useLiveUpdates";

const normaliseIdentityPart = (value: string | undefined): string =>
  value?.trim().toUpperCase() ?? "";

export const LiveUpdatesProvider = ({ children }: PropsWithChildren) => {
  const session = useSession();
  const enabled = useReleaseManagement(ReleaseFlag.releaseLiveUpdateGateway);
  const tokenOperation = useApiWithReject(api.hooks.auth.getLiveUpdateToken());
  const requestContext = session.current.context;
  const latestRequest = useRef({
    send: tokenOperation.send,
    context: requestContext,
  });

  // Keep renewed credentials current without recreating the connection on render.
  useEffect(() => {
    latestRequest.current = { send: tokenOperation.send, context: requestContext };
  }, [requestContext, tokenOperation.send]);

  const requestToken = useCallback(async (signal: AbortSignal) => {
    if (signal.aborted) throw new DOMException("Request aborted", "AbortError");
    const { send, context } = latestRequest.current;
    const result = await send(context, {});
    // The generated RPC cannot be aborted, so discard a late response instead.
    if (signal.aborted) throw new DOMException("Request aborted", "AbortError");
    return result;
  }, []);

  const userUuid = session.current.site?.user?.uuid?.trim() ?? "";
  const tenantClientCode = normaliseIdentityPart(
    session.current.credential?.clientCode ??
      session.current.site?.tenant?.code,
  );
  const carParkCode = normaliseIdentityPart(session.current.site?.home?.code);
  // Tabs issuing tokens through different APIs must not share a connection.
  const identity =
    userUuid && tenantClientCode
      ? `${requestContext.url}|${userUuid.toLowerCase()}|${tenantClientCode}|${carParkCode || "NO-HOME"}`
      : null;

  const client = useMemo(
    () =>
      // Construction stores this callback; it is only called after subscription.
      // eslint-disable-next-line react-hooks/refs
      createLiveUpdatesClient({
        enabled,
        diagnosticsEnabled: import.meta.env.DEV,
        identity,
        requestToken,
      }),
    [enabled, identity, requestToken],
  );

  useEffect(() => {
    client.start();
    return () => client.dispose();
  }, [client]);

  return (
    <LiveUpdatesClientContext.Provider value={client}>
      {children}
    </LiveUpdatesClientContext.Provider>
  );
};
